import React, { useState, useEffect, useCallback, useRef } from "react";
import "./styles/_index.scss";
interface GeographicCoordinates {
  lat: string;
  lng: string;
}
interface SectionConfig {
  id: string;
  label: string;
}
const Satellite: React.FC = () => {
  const SECTIONS: SectionConfig[] = [
    { id: "IR", label: "Infra-Red Image" },
    { id: "V", label: "Visible Image" },
  ];
  const [section, setSection] = useState<string>(SECTIONS[0].id);
  const [imageIndex, setImageIndex] = useState<number>(1);
  const [currentImageId, setCurrentImageId] = useState<string>("");
  const [imageSrc, setImageSrc] = useState<string>("");
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [loadError, setLoadError] = useState<boolean>(false);
  const [errorDetails, setErrorDetails] = useState<string>("");
  const [availableImages, setAvailableImages] = useState<string[]>([]);
  const [userGeoCords, setUserGeoCords] = useState<GeographicCoordinates>({
    lat: "",
    lng: "",
  });
  const isInitialLoadRef = useRef(true);
  const currentSectionRef = useRef(section);
  const availableImagesRef = useRef(availableImages);
  const currentImageIdRef = useRef(currentImageId);
  const [navigationStep, setNavigationStep] = useState(0);
  const [actualFilename, setActualFilename] = useState<string>("");
  const IMAGES_PER_SET = 8;
  const customRound = (value: number): number => {
    return Math.round(value);
  };
  const getNearestLatLon = (lat: number, lon: number) => {
    const nearestLat = Math.round(Math.abs(lat) / 5) * 5;
    const nearestLon = Math.round(lon / 5) * 5;
    const diffLat = Math.abs(lat) - nearestLat;
    const diffLon = lon - nearestLon;
    const claty =
      lat > 0
        ? customRound((5 - diffLat) * 55.6 + 22)
        : customRound((5 + diffLat) * 55.6 + 22);
    const clatx = customRound((5 + diffLon) * 55.6 + 22);
    return { nearestLat, nearestLon, clatx, claty };
  };

  const extractImageIdFromUrl = (url: string): string | null => {
    try {
      if (url.includes("satpic-")) {
        const match = url.match(/satpic-[\w-]*\d{4}/);
        return match ? match[0] : null;
      }
      return null;
    } catch (e) {
      console.error("Error extracting image ID from URL:", e);
      return null;
    }
  };

  const fetchAvailableImages = useCallback(async (currentSection: string) => {
    try {
      const response = await fetch(
        `${
          process.env.REACT_APP_BACKEND_IP || ""
        }/api/api/available_images/${currentSection}/`
      );
      if (!response.ok) {
        throw new Error(`Failed to fetch images for ${currentSection}`);
      }
      const imagesData = await response.json();
      const images = imagesData.images || [];
      setAvailableImages(images);
      availableImagesRef.current = images;
      return images;
    } catch (error) {
      console.error(
        `Error fetching available images for ${currentSection}:`,
        error
      );
      setErrorDetails(
        `Failed to load images: ${
          error instanceof Error ? error.message : "Unknown error"
        }`
      );
      setAvailableImages([]);
      availableImagesRef.current = [];
      return [];
    }
  }, []);

  const extractDisplayName = (filename: string, section: string): string => {
    const parts = filename.replace(".png", "").split("-");
    const sliceIndex = section === "V" ? 3 : 2;
    return parts.length > sliceIndex
      ? parts.slice(sliceIndex).join(" ")
      : filename.replace(".png", "");
  };
  const fetchImageByCoordinates = useCallback(
    async (currentSection: string, coords: GeographicCoordinates) => {
      setIsLoading(true);
      setLoadError(false);
      setErrorDetails("");
      try {
        if (!coords.lat || !coords.lng) {
          throw new Error("Missing coordinates for image fetch");
        }
        const lat = parseFloat(coords.lat);
        const lon = parseFloat(coords.lng);
        const { nearestLat, nearestLon, clatx, claty } = getNearestLatLon(
          lat,
          lon
        );
        const response = await fetch(
          `${
            process.env.REACT_APP_BACKEND_IP || ""
          }/api/api/display_image/${currentSection}/static-0001/?lat=${
            coords.lat
          }&lon=${coords.lng}&mode=api`
        );
        if (!response.ok) {
          throw new Error(
            `Failed to fetch image by coordinates: ${response.status} ${response.statusText}`
          );
        }
        const data = await response.json();
        if (data && data.image_url) {
          setImageSrc(data.image_url);
          setActualFilename(
            extractDisplayName(data.actual_filename, currentSection)
          );
          const imageNameFromUrl =
            data.matched_image_id ||
            extractImageIdFromUrl(data.image_url) ||
            availableImagesRef.current[0];
          if (!imageNameFromUrl) {
            throw new Error("Could not determine image ID");
          }
          setCurrentImageId(imageNameFromUrl);
          currentImageIdRef.current = imageNameFromUrl;
          const foundIndex = availableImagesRef.current.findIndex(
            (img) => img === imageNameFromUrl
          );
          if (foundIndex >= 0) {
            setImageIndex(foundIndex + 1);
          } else {
            setImageIndex(1);
          }
        } else {
          throw new Error("Invalid image data received");
        }
      } catch (error) {
        console.error("Error fetching image by coordinates:", error);
        setImageSrc("");
        setLoadError(true);
        setErrorDetails(
          error instanceof Error ? error.message : "Unknown error"
        );
      } finally {
        setIsLoading(false);
      }
    },
    []
  );
  const fetchImageById = useCallback(
    async (currentSection: string, imageId: string) => {
      setIsLoading(true);
      setLoadError(false);
      setErrorDetails("");
      try {
        if (!userGeoCords.lat || !userGeoCords.lng) {
          throw new Error("Missing coordinates from backend");
        }
        const response = await fetch(
          `${
            process.env.REACT_APP_BACKEND_IP || ""
          }/api/api/display_image/${currentSection}/${imageId}/?lat=${
            userGeoCords.lat
          }&lon=${userGeoCords.lng}&mode=api`
        );
        if (!response.ok) {
          throw new Error(
            `Failed to fetch image: ${response.status} ${response.statusText}`
          );
        }
        const data = await response.json();
        if (data && data.image_url) {
          setImageSrc(data.image_url);
          setActualFilename(
            extractDisplayName(data.actual_filename, currentSection)
          );
          setCurrentImageId(imageId);
          currentImageIdRef.current = imageId;
        } else {
          throw new Error("Invalid image data received");
        }
      } catch (error) {
        console.error("Error fetching image by ID:", error);
        setImageSrc("");
        setLoadError(true);
        setErrorDetails(
          error instanceof Error ? error.message : "Unknown error"
        );
      } finally {
        setIsLoading(false);
      }
    },
    [userGeoCords]
  );
  const fetchInitialData = useCallback(async () => {
    try {
      const fid = localStorage.getItem("fid") || "default";
      const locationResponse = await fetch(
        `${process.env.REACT_APP_BACKEND_IP || ""}/api/cyclone/${fid}/`
      );
      if (!locationResponse.ok) {
        throw new Error(
          `Failed to fetch project location: ${locationResponse.status}`
        );
      }
      const locationData = await locationResponse.json();
      const { latitude, longitude } = locationData.project_location || {};
      if (latitude && longitude) {
        const coords = {
          lat: latitude.toString(),
          lng: longitude.toString(),
        };
        setUserGeoCords(coords);
        await fetchAvailableImages(section);
        await fetchImageByCoordinates(section, coords);
      } else {
        throw new Error("No coordinates received from backend");
      }
    } catch (error) {
      console.error("Error fetching initial data:", error);
      setErrorDetails(
        `Error initializing: ${
          error instanceof Error ? error.message : "Unknown error"
        }`
      );
      setIsLoading(false);
      setLoadError(true);
    }
  }, [section, fetchAvailableImages, fetchImageByCoordinates]);
  const handleSectionChange = useCallback(
    async (newSection: string) => {
      if (section !== newSection) {
        setSection(newSection);
        currentSectionRef.current = newSection;
        let currentIdNumber = 1;
        try {
          if (currentImageId) {
            const match = currentImageId.match(/\d+$/);
            if (match) {
              currentIdNumber = parseInt(match[0], 10);
            }
          }
        } catch (e) {
          console.error("Error extracting ID number:", e);
          currentIdNumber = 1;
        }
        const newImages = await fetchAvailableImages(newSection);
        let newImageId: string;
        if (newSection === "IR") {
          newImageId = `satpic-${currentIdNumber.toString().padStart(4, "0")}`;
        } else {
          newImageId = `satpic-vis-${currentIdNumber
            .toString()
            .padStart(4, "0")}`;
        }
        if (newImages.includes(newImageId)) {
          const newIndex = newImages.indexOf(newImageId) + 1;
          setImageIndex(newIndex);
          await fetchImageById(newSection, newImageId);
        } else {
          if (newImages.length > 0) {
            setImageIndex(1);
            await fetchImageById(newSection, newImages[0]);
          } else {
            await fetchImageByCoordinates(newSection, userGeoCords);
          }
        }
      }
    },
    [
      section,
      currentImageId,
      fetchAvailableImages,
      fetchImageById,
      fetchImageByCoordinates,
      userGeoCords,
    ]
  );
  const handleNextImage = useCallback(() => {
    const currentImages = availableImagesRef.current;
    const currentSection = currentSectionRef.current;
    if (currentImages.length === 0) return;
    const proposedIndex = imageIndex + 8;
    if (proposedIndex > currentImages.length) {
      return; // stay on current image
    }
    setImageIndex(proposedIndex);
    const newImageId = currentImages[proposedIndex - 1];
    fetchImageById(currentSection, newImageId);
  }, [imageIndex, fetchImageById]);

  const handlePrevImage = useCallback(() => {
    const currentImages = availableImagesRef.current;
    const currentSection = currentSectionRef.current;
    if (currentImages.length === 0) return;
    const proposedIndex = imageIndex - 8;
    if (proposedIndex < 1) {
      return; // stay on current image
    }
    setImageIndex(proposedIndex);
    const newImageId = currentImages[proposedIndex - 1];
    fetchImageById(currentSection, newImageId);
  }, [imageIndex, fetchImageById]);

  const handlePrevSet = useCallback(() => {
    const currentImages = availableImagesRef.current;
    const currentSection = currentSectionRef.current;
    if (currentImages.length === 0) return;
    const currentIndex = imageIndex;
    const currentSet = Math.ceil(currentIndex / IMAGES_PER_SET);
    const currentSetEnd = currentSet * IMAGES_PER_SET;
    if (currentIndex >= currentSetEnd) {
      return;
    }
    const newIndex = currentIndex + 1;
    setImageIndex(newIndex);
    const newImageId = currentImages[newIndex - 1];
    fetchImageById(currentSection, newImageId);
  }, [imageIndex, fetchImageById]);
  useEffect(() => {
    setNavigationStep(0);
  }, [section]);
  const handleNextSet = useCallback(() => {
    const currentImages = availableImagesRef.current;
    const currentSection = currentSectionRef.current;
    if (currentImages.length === 0) return;
    const currentIndex = imageIndex;
    const currentSet = Math.ceil(currentIndex / IMAGES_PER_SET);
    const currentSetStart = (currentSet - 1) * IMAGES_PER_SET + 1;
    if (currentIndex <= currentSetStart) {
      return;
    }
    const newIndex = currentIndex - 1;
    setImageIndex(newIndex);
    const newImageId = currentImages[newIndex - 1];
    fetchImageById(currentSection, newImageId);
  }, [imageIndex, fetchImageById]);
  useEffect(() => {
    if (isInitialLoadRef.current) {
      fetchInitialData();
      isInitialLoadRef.current = false;
    }
  }, [fetchInitialData]);
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "ArrowRight") {
        e.preventDefault();
        handleNextImage();
      }
      if (e.key === "ArrowLeft") {
        e.preventDefault();
        handlePrevImage();
      }
      if (e.key === "ArrowUp") {
        e.preventDefault();
        handlePrevSet();
      }
      if (e.key === "ArrowDown") {
        e.preventDefault();
        handleNextSet();
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [handleNextImage, handlePrevImage, handleNextSet, handlePrevSet]);
  return (
    <div className="outer-container">
      <div className="inner-container">
             <div className="image-frame">
          <button
            style={{
              position: "absolute",
              top: "-5px",
              left: "50%",
              transform: "translateX(-50%)",
              width: "40px",
              height: "40px",
              backgroundColor: "#000",
              color: "#fff",
              border: "2px solid #ccc",

              cursor: "pointer",
              fontSize: "18",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              zIndex: 10,
              padding: 0,
            }}
            className="nav-btn nav-btn-top"
            onClick={handleNextSet}
            aria-label="Next Set"
            disabled={isLoading}
          >
            ⬆
          </button>
          <button
            style={{
              position: "absolute",
              left: "-5px",
              top: "50%",
              transform: "translateY(-50%)",
              width: "40px",
              height: "40px",
              backgroundColor: "#000",
              color: "#fff",
              border: "2px solid #ccc",
              cursor: "pointer",
              fontSize: "18",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              zIndex: 10,
              padding: 0,
            }}
            className="nav-btn nav-btn-prev"
            onClick={handlePrevImage}
            aria-label="Previous Image"
            disabled={isLoading}
          >
            ⬅
          </button>
          {isLoading ? (
            <div className="loading-indicator">Loading satellite image...</div>
          ) : loadError ? (
            <div className="error-message">
              Failed to load image. {errorDetails}
            </div>
          ) : (
            imageSrc && (
              <img
                src={imageSrc}
                alt={`Satellite imagery (${section} section)`}
                className="center-image"
                onError={() => setLoadError(true)}
              />
            )
          )}
          <button
            style={{
              position: "absolute",
              right: "-5px",
              top: "50%",
              transform: "translateY(-50%)",
              width: "40px",
              height: "40px",
              backgroundColor: "#000",
              color: "#fff",
              border: "2px solid #ccc",
              cursor: "pointer",
              fontSize: "18",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              zIndex: 10,
              padding: 0,
            }}
            className="nav-btn nav-btn-next"
            onClick={handleNextImage}
            aria-label="Next Image"
            disabled={isLoading}
          >
            ⮕
          </button>
          <button
            style={{
              position: "absolute",
              bottom: "-5px",
              left: "50%",
              transform: "translateX(-50%)",
              width: "40px",
              height: "40px",
              backgroundColor: "#000",
              color: "#fff",
              border: "2px solid #ccc",
              cursor: "pointer",
              fontSize: "18",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              zIndex: 20,
              padding: 0,
            }}
            className="nav-btn nav-btn-bottom"
            onClick={handlePrevSet}
            aria-label="Previous Set"
            disabled={isLoading}
          >
            ⬇
          </button>
          <div className="section-buttons">
            {SECTIONS.map((sectionConfig) => (
              <button
                key={sectionConfig.id}
                className={`section-btn ${
                  section === sectionConfig.id ? "active" : ""
                }`}
                onClick={() => handleSectionChange(sectionConfig.id)}
              >
                {sectionConfig.label}
              </button>
            ))}
          </div>
          {!isLoading && !loadError && imageSrc && (
            <div className="image-name-display">
              {actualFilename || currentImageId || "Satellite Image"}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
export default Satellite;
